import cn from 'classnames';
import { animated, useSpring, useTrail } from 'react-spring';
import { useRef, useState } from 'react';
import { ReactComponent as ChevronIcon } from '@assets/svg/chevron.svg';
import { IOptionItem } from '../../interfaces';
import styles from './dropdown.module.scss';

interface IDropdownProps {
    options: IOptionItem[];
    // eslint-disable-next-line no-unused-vars
    onChange: (value: string) => void;
    className?: string;
}

const Dropdown = ({
    options,
    onChange,
    className = '',
}: IDropdownProps): JSX.Element => {
    /* Hooks */

    const [isDown, setIsDown] = useState(false);
    const [selected, setSelected] = useState<IOptionItem>(options[0]);
    const contentRef = useRef<HTMLDivElement>(null);

    /* Animations */

    const contentSpring = useSpring({
        opacity: isDown ? 1 : 0,
        overflow: isDown && contentRef.current ? 'visible' : 'hidden',
        maxHeight:
            isDown && contentRef.current ? contentRef.current.scrollHeight : 0,
        y: isDown ? 10 : 0,
    });

    const optionsTrail = useTrail(options.length, {
        opacity: isDown ? 1 : 0,
        x: isDown ? 0 : 50,
    });

    /* Handlers */

    const handleClick = (): void => {
        setIsDown((prevState) => !prevState);
    };

    const handleSelect = (value: IOptionItem): void => {
        setSelected(value);
        setIsDown(false);

        onChange(value.value);
    };

    /* Components */

    const Items = optionsTrail.map((style, idx) => (
        <animated.div
            key={options[idx].value}
            className={styles.dropdown__item}
            onClick={() => handleSelect(options[idx])}
            {...{ style }}
        >
            {options[idx].name}
        </animated.div>
    ));

    /* ClassNames */

    const buttonClasses: string = cn(styles.dropdown__button, {
        [styles.down]: isDown,
    });

    return (
        <div className={cn(styles.dropdown, className)}>
            <div className={buttonClasses} onClick={handleClick}>
                <span>{selected.name}</span>
                <ChevronIcon />
            </div>
            <animated.div
                className={styles.dropdown__content}
                ref={contentRef}
                style={contentSpring}
            >
                {Items}
            </animated.div>
        </div>
    );
};

export default Dropdown;
